import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import { Grid } from '@material-ui/core';

import { useMutation } from '@apollo/client';

import { Button } from 'core/components/button';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import { sweepAccountFieldPermissions, sweepPaymentMethodTypeLabel } from 'core/helpers/sweep-account';
import CollapsibleSection from './collapsible-section';
import PaymentMethodFields from './payment-method-fields';
import { useSweepPaymentToken } from './use-sweep-payment-token';
import {
  ADD_SWEEP_PAYMENT_METHOD,
  REMOVE_SWEEP_PAYMENT_METHOD,
  SET_DEFAULT_SWEEP_PAYMENT_METHOD
} from './sweep-account.queries';
import useStyles from './sweep-account.styles';

const describePaymentMethod = (pm) => `${sweepPaymentMethodTypeLabel(pm)} •••• ${pm.last4 || '????'}`;

const ManageAccount = ({ account, onUpdated }) => {
  const classes = useStyles();
  const getPaymentToken = useSweepPaymentToken();
  const [removePaymentMethod] = useMutation(REMOVE_SWEEP_PAYMENT_METHOD);
  const [setDefaultPaymentMethod] = useMutation(SET_DEFAULT_SWEEP_PAYMENT_METHOD);
  const [addPaymentMethod] = useMutation(ADD_SWEEP_PAYMENT_METHOD);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const runOnMethod = async (paymentMethodId, mutation, pick) => {
    setError(null);
    setBusyId(paymentMethodId);
    try {
      const { data } = await mutation({ variables: { paymentMethodId } });
      onUpdated(data[pick]);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setBusyId(null);
    }
  };

  const onAdd = async (values, { setSubmitting, resetForm }) => {
    setError(null);
    try {
      const { paymentMethodToken, paymentMethodType } = await getPaymentToken(values.paymentMethodType);
      const { data } = await addPaymentMethod({
        variables: { paymentMethodToken, paymentMethodType, makeDefault: values.makeDefault }
      });
      onUpdated(data.addSweepPaymentMethod);
      resetForm();
    } catch (e) {
      setError(e.message || 'Failed to add payment method');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CollapsibleSection title={`${account.contact?.name || account.affinityStem} — Payment Methods`}>
        <Grid item xs={12}>
          {account.paymentMethods.length === 0 && <Label type="infoValue">No payment methods on file yet.</Label>}
          {account.paymentMethods.map((pm) => (
            <div key={pm.id} className={classes.paymentMethodRow}>
              <Label type="infoValue">
                {describePaymentMethod(pm)}
                {pm.isDefault && <span className={classes.defaultBadge}>Default</span>}
              </Label>
              <span className={classes.rowActions}>
                {!pm.isDefault && (
                  <Button
                    variant="outlined"
                    color="primary"
                    loading={busyId === pm.id}
                    onClick={() => runOnMethod(pm.id, setDefaultPaymentMethod, 'setDefaultSweepPaymentMethod')}
                  >
                    Make default
                  </Button>
                )}
                <Button
                  variant="outlined"
                  loading={busyId === pm.id}
                  onClick={() => runOnMethod(pm.id, removePaymentMethod, 'removeSweepPaymentMethod')}
                >
                  Remove
                </Button>
              </span>
            </div>
          ))}
        </Grid>
      </CollapsibleSection>

      <Formik initialValues={{ paymentMethodType: 'card', makeDefault: false }} onSubmit={onAdd}>
        {({ isSubmitting }) => (
          <Form>
            <CollapsibleSection title="Add a Payment Method">
              <PaymentMethodFields />
              <FormField
                permissions={sweepAccountFieldPermissions}
                xs={12}
                type="checkbox"
                name="makeDefault"
                mode="light"
                label="Set as default"
              />
              <Grid item xs={12}>
                <div className={classes.actions}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    className={classes.submitButton}
                    loading={isSubmitting}
                  >
                    Add payment method
                  </Button>
                </div>
              </Grid>
            </CollapsibleSection>
          </Form>
        )}
      </Formik>

      {error && (
        <Label type="infoValue" className={classes.error}>
          {error}
        </Label>
      )}
    </>
  );
};

ManageAccount.propTypes = {
  account: PropTypes.object.isRequired,
  onUpdated: PropTypes.func.isRequired
};

export default ManageAccount;
